/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { compose, bindActionCreators } from 'redux';
import { useHeader, useHeaderLinks } from 'providers/AreasProvider';
import useDebounce from 'utils/hooks/useDebounce';
import { withTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Box, Grid } from '@mui/material';
import { IconButton, Card, Typography } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import { GridOn, FilterList, ArrowBack } from '@mui/icons-material';
import * as folderActions from '../actionCreator';
import * as dictionariesActions from 'actions/actionCreators/dictionariesActions';
import { getResponsibleFolders } from '../selectors';
import FolderAllocateResponsibleList from './FolderAllocateResponsibleList';
import FolderAllocateResponsibleFilters from './FolderAllocateResponsibleFilters';
import NotFound from 'components/common/NotFound';
import documentTypeIds from 'constants/documentTypeIds';

const defaultFilters = {
  clientName: '',
  folderNumber: '',
  isAssignedResponsible: false,
  stateId: null
};

const FolderAllocateResponsibleContainer = ({
  t,
  history,
  responsibleChangeFolders,
  filters,
  onFiltersChanged,
  pager,
  loading,
  totalCount,
  onChangePage,
  onChangeRowsPerPage,
  actions,
  enableBulkResponsibleAssignation
}) => {
  const [editFilters, setEditFilters] = useState(defaultFilters);
  const [saving, setSaving] = useState(false);

  const [responsibleId, setResponsibleId] = useState(null);
  const [responsibleName, setResponsibleName] = useState(null);
  const [selectedFolderList, setSelectedFolderList] = useState([]);

  const [, setHeader] = useHeader();
  const [, setHeaderLinks] = useHeaderLinks();
  const debouncedEditFilters = useDebounce(editFilters, 250);

  const handleBackToList = useCallback(() => {
    history.push('/folders');
  }, [history]);

  const handleAllocateFolders = useCallback(
    (item, value) => {
      if (value === true)
        setSelectedFolderList([
          ...selectedFolderList,
          { documentId: item.documentId, siteId: item.siteId, folderNumber: item.folderNumber }
        ]);
      else if (value === false) {
        const newList = [...selectedFolderList];
        newList.forEach((elem, idx) => {
          if (elem.documentId === item.documentId && elem.siteId === item.siteId) newList.splice(idx, 1);
        });
        setSelectedFolderList(newList);
      }
    },
    [selectedFolderList]
  );

  const loadStates = useCallback(() => {
    return actions.loadStates(documentTypeIds.Folder);
  }, [actions]);

  const loadResponsibles = useCallback(
    input => {
      return actions.loadRiskResponsibles(input);
    },
    [actions]
  );

  const handleChangeFilterValue = (name, value) => {
    setEditFilters({ ...editFilters, [name]: value });
  };

  const handleResponsibleChange = useCallback(value => {
    setResponsibleId(value?.responsibleId || null);
    setResponsibleName(value?.name || null);
  }, []);

  const handleResponsibleSave = useCallback(() => {
    setSaving(true);
    actions.setResponsibleOnFolders(selectedFolderList, responsibleId, responsibleName).then(() => {
      setSaving(false);
    });
    setResponsibleId(null);
    setResponsibleName(null);
    setSelectedFolderList([]);
  }, [actions, responsibleId, responsibleName, selectedFolderList]);

  const handleSelectAllFolders = useCallback(() => {
    const folderList =
      responsibleChangeFolders.length > 0
        ? responsibleChangeFolders.map(folder => ({
            documentId: folder.documentId,
            siteId: folder.siteId,
            folderNumber: folder.folderNumber
          }))
        : [];
    setSelectedFolderList([...selectedFolderList, ...folderList]);
  }, [responsibleChangeFolders]);

  const handleDeselectAll = useCallback(() => {
    setSelectedFolderList([]);
  }, []);

  const handleRefresh = () => {
    actions.refreshResponsibleFolders(filters, pager.pageSize);
    onFiltersChanged(editFilters);
  };

  useEffect(() => {
    enableBulkResponsibleAssignation &&
      setHeader(
        <Typography fontWeight='bold' variant='subtitle1'>
          {t('FolderAllocateResponsible.Title')}
        </Typography>
      );
  }, [t, setHeader]);

  useEffect(() => {
    setHeaderLinks(
      <Box px={1}>
        <IconButton
          id='folderAllocateResponsible_backToListBtn'
          color='primary'
          variant='text'
          onClick={handleBackToList}
          tooltip={t('OfferAction.BackToList')}
        >
          <ArrowBack color='inherit' />
        </IconButton>
      </Box>
    );
  }, [t, setHeaderLinks, handleBackToList]);

  useEffect(() => {
    setEditFilters(filters);
    actions.loadFolders(filters, pager.page, pager.pageSize);
  }, []);

  useEffect(() => {
    actions.loadFolders(filters, pager.page, pager.pageSize);
  }, [actions, filters, pager.page, pager.pageSize]);

  useEffect(() => {
    onFiltersChanged(editFilters);
  }, [debouncedEditFilters]);

  return !enableBulkResponsibleAssignation ? (
    <NotFound title='PageNotFound'></NotFound>
  ) : (
    <Grid container>
      <Card icon={FilterList} title={t('FolderAllocateResponsible.FiltersTitle')}>
        <FolderAllocateResponsibleFilters filters={editFilters} onChangeFilterValue={handleChangeFilterValue} loadStates={loadStates} />
      </Card>
      <Card
        icon={GridOn}
        title={
          <Typography variant='subtitle1' fontWeight='bold'>
            {t('FolderAllocateResponsible.ChoseResponsible')}
            {selectedFolderList.length > 0
              ? selectedFolderList.map((f, index) => {
                  if (index !== selectedFolderList.length - 1) return f.folderNumber + ', ';
                  else return `${f.folderNumber}`;
                })
              : ''}
          </Typography>
        }
      >
        <FolderAllocateResponsibleList
          responsibleId={responsibleId}
          responsibleChangeFolders={responsibleChangeFolders}
          loadResponsibles={loadResponsibles}
          handleResponsibleChange={handleResponsibleChange}
          handleResponsibleSave={handleResponsibleSave}
          handleAllocateFolders={handleAllocateFolders}
          handleSelectAllFolders={handleSelectAllFolders}
          handleDeselectAll={handleDeselectAll}
          selectedFolderList={selectedFolderList}
          loading={loading}
          saving={saving}
        />
        <PaginationWrapper
          loading={loading}
          count={totalCount}
          pageSize={pager.pageSize}
          page={pager.page}
          onRefresh={handleRefresh}
          onPageChange={onChangePage}
          onRowsPerPageChange={onChangeRowsPerPage}
        />
      </Card>
    </Grid>
  );
};

FolderAllocateResponsibleContainer.propTypes = {
  t: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  pager: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  onFiltersChanged: PropTypes.func.isRequired,
  actions: PropTypes.object.isRequired,
  responsibleChangeFolders: PropTypes.array.isRequired,
  loading: PropTypes.bool.isRequired,
  totalCount: PropTypes.number.isRequired,
  onChangeRowsPerPage: PropTypes.func.isRequired,
  onChangePage: PropTypes.func.isRequired,
  enableBulkResponsibleAssignation: PropTypes.bool.isRequired
};

function mapStateToProps(state, ownProps) {
  const { filters, pager } = ownProps;
  const {
    loading,
    loaded,
    content: responsibleChangeFolders,
    totalCount
  } = getResponsibleFolders(filters, pager.page, pager.pageSize, state);
  const enableBulkResponsibleAssignation = state.applicationFeatures.bulkResponsibleAssignation;
  return { loading, loaded, responsibleChangeFolders, totalCount, enableBulkResponsibleAssignation };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators({ ...folderActions, ...dictionariesActions }, dispatch),
    dispatch
  };
}

const AllocateResponsibleContainerConnected = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTranslation()
)(FolderAllocateResponsibleContainer);

const AllocateResponsibleContainerWrapper = props => {
  const [pager, setPager] = useState({ page: 1, pageSize: 10 });
  const [filters, setFilters] = useState(defaultFilters);

  const pagerUpdater = useCallback(
    (prop, value) => {
      return prop === 'pageSize' ? { ...pager, [prop]: value, page: 1 } : { ...pager, [prop]: value };
    },
    [pager]
  );

  const onChangePage = page => {
    setPager(pagerUpdater('page', ++page));
  };

  const onChangeRowsPerPage = pageSize => {
    setPager(pagerUpdater('pageSize', pageSize));
  };

  const onFiltersChanged = useCallback(
    filters => {
      setFilters({ ...filters });
      setPager(pagerUpdater('page', 1));
    },
    [pagerUpdater]
  );

  return (
    <AllocateResponsibleContainerConnected
      {...props}
      filters={filters}
      pager={pager}
      onFiltersChanged={onFiltersChanged}
      onChangePage={onChangePage}
      onChangeRowsPerPage={onChangeRowsPerPage}
    />
  );
};

export default withTranslation()(AllocateResponsibleContainerWrapper);
